'use client';
import {
  type Dispatch,
  type SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { PageBlock } from '@depop/web-ui-kit/PageBlock';
import {
  type ConsentChangeEventPayload,
  ViewState,
} from '@transcend-io/airgap.js-types';

import { RefererContext } from '@/modules/activityTracker/RefererProvider';
import { getReferer } from '@/modules/activityTracker/referers';
import {
  TRANSCEND_VIEW_STATE_CHANGE_EVENT_NAME,
  DEFAULT_TRANSCEND_VIEW_STATE,
  TRANSCEND_CONSENT_CHANGE_EVENT_NAME,
} from '@/modules/consent/constants';
import { useConsentReconciliation } from '@/modules/consent/hooks/useConsentReconciliation';
import { useConsentTracking } from '@/modules/consent/hooks/useConsentTracking';

export function TranscendCookieBanner({
  setShowBanner,
  manualShowBanner = false,
  setManualShowBanner,
}: {
  setShowBanner: Dispatch<SetStateAction<boolean>>;
  manualShowBanner: boolean;
  setManualShowBanner: Dispatch<SetStateAction<boolean>>;
}) {
  const [isBannerShowing, setIsBannerShowing] = useState(false);
  const { transcend, airgap, reconcileConsentMutation } =
    useConsentReconciliation(true);
  const {
    sendCookieBannerViewEvent,
    sendCookieModalActionEvent,
    sendAcceptAllEvent,
    sendRejectAllEvent,
    sendCookieModalManageEvent,
  } = useConsentTracking();
  const referer = useContext(RefererContext) || '';
  const transitionFrom = getReferer(referer);

  function handleViewStateChange(event: CustomEvent) {
    const { detail } = event;
    const { viewState, previousViewState } = detail;
    const cameFromBanner =
      previousViewState === DEFAULT_TRANSCEND_VIEW_STATE ||
      previousViewState === ViewState.AcceptOrRejectAll;

    // this will show the opacity for most times when the banner comes visible
    // but not the first time on a new page load for a new user
    if (viewState !== ViewState.Hidden) {
      setIsBannerShowing(true);
    }

    if (cameFromBanner) {
      if (viewState === ViewState.CompleteOptions) {
        // send the AT event
        sendCookieModalManageEvent();
        transcend?.showConsentManager({
          viewState: ViewState.CompleteOptionsToggles,
        });
      }
    }

    if (viewState === ViewState.Hidden) {
      reconcileConsentMutation.mutate(false);
      setManualShowBanner(false);
      setShowBanner(false);
    }
  }

  function handleConsentChangeActivityTrackerEvents(
    event: ConsentChangeEventPayload
  ) {
    const { detail } = event;
    const { consent } = detail;
    const viewState = transcend?.getViewState();
    const allTrue = Object.values({ ...consent?.purposes })
      .filter((v) => typeof v !== 'string')
      .every((v) => v === true);
    const allFalse = Object.values({ ...consent?.purposes })
      .filter((v) => typeof v !== 'string')
      .every((v) => v === false);
    if (viewState === DEFAULT_TRANSCEND_VIEW_STATE) {
      if (allTrue) {
        sendAcceptAllEvent();
      }
      if (allFalse) {
        sendRejectAllEvent();
      }
    }
    if (viewState === ViewState.CompleteOptions && consent) {
      sendCookieModalActionEvent(consent);
    }
  }

  useEffect(() => {
    if (!transcend || !airgap) {
      return;
    }
    transcend.addEventListener(
      TRANSCEND_VIEW_STATE_CHANGE_EVENT_NAME,
      handleViewStateChange
    );
    airgap.addEventListener(
      TRANSCEND_CONSENT_CHANGE_EVENT_NAME,
      handleConsentChangeActivityTrackerEvents
    );
    transcend.autoShowConsentManager();
    return () => {
      transcend.removeEventListener(
        TRANSCEND_VIEW_STATE_CHANGE_EVENT_NAME,
        handleViewStateChange
      );
      airgap.removeEventListener(
        TRANSCEND_CONSENT_CHANGE_EVENT_NAME,
        // @ts-ignore -- the event type in the function doesn't work here
        handleConsentChangeActivityTrackerEvents
      );
    };
  }, [transcend, airgap]);

  useEffect(() => {
    if (isBannerShowing) {
      sendCookieBannerViewEvent(transitionFrom);
    }
  }, [isBannerShowing, transitionFrom]);

  // shows the banner if the user clicks cookies link
  if (manualShowBanner) {
    transcend?.showConsentManager();
  }

  {
    return isBannerShowing ? (
      <div id="cookies-modal">
        <PageBlock isElevated />
      </div>
    ) : null;
  }
}
